<script lang="ts">
	import { t, type Language } from '$lib/i18n';
	import LanguageSwitcher from './LanguageSwitcher.svelte';
	import Logo from './Logo.svelte';
	import { captureEvent } from '$lib/analytics/posthog';
	import { onMount, getContext } from 'svelte';
	import { afterNavigate } from '$app/navigation';
	import type { ThemeStore } from '$lib/stores/theme.svelte';

	interface Props {
		lang: Language;
	}

	let { lang }: Props = $props();
	let tr = $derived(t(lang));
	const theme = getContext<ThemeStore>('theme');
	let currentTheme = $derived(theme.current);

	let scrolled = $state(false);
	let mobileMenuOpen = $state(false);
	let menuRef: HTMLElement | undefined = $state(undefined);
	let ticking = false;

	// Close mobile menu after any navigation completes
	afterNavigate(() => {
		mobileMenuOpen = false;
	});

	onMount(() => {
		const updateScrolled = () => {
			scrolled = window.scrollY > 20;
			ticking = false;
		};
		const handleScroll = () => {
			if (!ticking) {
				requestAnimationFrame(updateScrolled);
				ticking = true;
			}
		};
		const handleKeydown = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && mobileMenuOpen) {
				mobileMenuOpen = false;
			}
		};
		window.addEventListener('scroll', handleScroll, { passive: true });
		window.addEventListener('keydown', handleKeydown);
		return () => {
			window.removeEventListener('scroll', handleScroll);
			window.removeEventListener('keydown', handleKeydown);
		};
	});

	function handleToggleTheme() {
		const newTheme = theme.toggle();
		captureEvent('theme_toggled', { theme: newTheme });
	}

	function handleNavClick(item: string) {
		captureEvent('nav_item_clicked', { item, lang });
	}

	function handleCtaClick(cta: string) {
		captureEvent('cta_clicked', { cta, page: 'header', lang });
	}

	let navLinks = $derived([
		{ href: `/${lang}/services`, label: tr('nav_services') },
		{ href: `/${lang}/analytics`, label: tr('nav_platform') },
		{ href: `/${lang}/sensors`, label: tr('nav_sensors') },
		{ href: `/${lang}/about`, label: tr('nav_about') }
	]);
</script>

<!-- Skip to content link -->
<a
	href="#main-content"
	class="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[60] focus:px-4 focus:py-2 focus:bg-ameta-green focus:text-white focus:rounded-lg focus:text-sm focus:font-medium"
>
	Skip to content
</a>

<header
	class="site-header fixed top-0 left-0 right-0 z-50 transition-all duration-700 {scrolled
		? 'header-scrolled bg-slate-950/95 md:bg-slate-950/70 md:backdrop-blur-2xl border-b border-white/[0.08] shadow-lg shadow-black/10'
		: 'bg-transparent'}"
	style="transition-timing-function: cubic-bezier(0.25, 1, 0.5, 1);"
>
	<nav class="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
		<a href="/{lang}" class="flex items-center group text-[#EEEFE3]" aria-label="Ameta home">
			<Logo size="md" class="header-logo" />
		</a>

		<!-- Desktop nav -->
		<div class="hidden md:flex items-center gap-1">
			{#each navLinks as link}
				<a
					href={link.href}
					data-sveltekit-preload-data="hover"
					onclick={() => handleNavClick(link.label)}
					class="relative px-4 py-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-all duration-300 text-sm font-medium rounded-lg group overflow-hidden"
				>
					<span class="relative z-10">{link.label}</span>
					<div
						class="absolute inset-0 bg-white/[0.06] opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"
					></div>
					<div
						class="absolute bottom-1 left-4 right-4 h-px bg-white/30 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"
					></div>
				</a>
			{/each}
			<LanguageSwitcher {lang} />
			<button
				onclick={handleToggleTheme}
				class="p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors duration-300 rounded-lg hover:bg-white/[0.06]"
				aria-label="Toggle theme"
			>
				{#if currentTheme === 'dark'}
					<svg
						aria-hidden="true"
						class="w-5 h-5"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="1.5"
							d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
						/>
					</svg>
				{:else}
					<svg
						aria-hidden="true"
						class="w-5 h-5"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="1.5"
							d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
						/>
					</svg>
				{/if}
			</button>
			<a
				href="/{lang}/#contact"
				onclick={() => handleCtaClick('header_contact')}
				class="ml-2 px-5 py-2.5 bg-gradient-to-br from-white to-slate-100 text-slate-950 rounded-xl font-semibold text-sm transition-all duration-500 hover:shadow-xl hover:shadow-white/15 hover:scale-105 hover:-translate-y-0.5"
			>
				{tr('nav_contact')}
			</a>
		</div>

		<!-- Mobile menu button -->
		<button
			class="md:hidden p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors duration-300"
			onclick={() => (mobileMenuOpen = !mobileMenuOpen)}
			aria-label="Toggle menu"
			aria-expanded={mobileMenuOpen}
		>
			<svg aria-hidden="true" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				{#if mobileMenuOpen}
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="1.5"
						d="M6 18L18 6M6 6l12 12"
					/>
				{:else}
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="1.5"
						d="M4 6h16M4 12h16M4 18h16"
					/>
				{/if}
			</svg>
		</button>
	</nav>

	<!-- Mobile menu -->
	{#if mobileMenuOpen}
		<nav
			bind:this={menuRef}
			class="mobile-menu md:hidden bg-slate-950 border-t border-white/[0.06]"
			aria-label="Mobile navigation"
		>
			<div class="px-6 py-4 space-y-2">
				{#each navLinks as link}
					<a
						href={link.href}
						onclick={() => (mobileMenuOpen = false)}
						class="block px-4 py-3 text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-white/[0.04] active:bg-white/[0.08] font-medium rounded-lg"
					>
						{link.label}
					</a>
				{/each}
				<div class="px-4 py-3">
					<LanguageSwitcher {lang} />
				</div>
				<a
					href="/{lang}/#contact"
					onclick={() => {
						handleCtaClick('mobile_contact');
						mobileMenuOpen = false;
					}}
					class="block px-4 py-3 bg-white text-slate-950 rounded-lg font-medium text-center mt-4 active:bg-slate-100"
				>
					{tr('nav_contact')}
				</a>
			</div>
		</nav>
	{/if}
</header>
